import { useQuery } from '@apollo/react-hooks';
import dayjs from 'dayjs';
import { GET_DRC_PAGINATED_NON_PROPOSED } from './GET_DRC_APPLICATIONS.gql';
import type { DRCNonProposedData, GetNonProposedData } from './types';
import { MONITOR_LIST_TEXT } from '../../../lang';
import { getOptionsLabel } from '../../../../Monitor/components';
import {
  modeOfStudyOptions,
  programmeTypeOptions,
} from '../../../../Application/components';
import { type Paginator } from '../../../../Nomination/graphql';
import { PAGE_TEXT } from '../../../../Nomination/components/lang';
import { FUNDING_STATUS_DISPLAY_STRING } from '../../../../ScholarshipManagement/constants/fundingStatus';

const PROGRAMME_LEVEL = MONITOR_LIST_TEXT.PROGRAMME_LEVEL;
const NO_INDICATION = 'Not Indicated';
const fundingSourceValues = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;

export const useGetNonProposed = (
  filter: any,
  sort: any,
  paginator: Paginator,
): GetNonProposedData => {
  const {
    data: applicationsData,
    loading,
    error,
    refetch,
  } = useQuery(GET_DRC_PAGINATED_NON_PROPOSED, {
    variables: { filter, sort, paginator },
    fetchPolicy: 'network-only',
  });

  const applicationsContent =
    applicationsData?.applicationViewsPaginated?.data ?? [];

  let nonProposedApplications = {
    data: [] as DRCNonProposedData[],
    totalPage: 1,
    count: 0,
  };

  if (!loading) {
    nonProposedApplications = {
      data: applicationsContent.map((element: any) => {
        const {
          id: applicationId,
          applicationNo,
          applicant,
          programmeChoice,
          hkpfsInformation,
          status,
        } = element ?? {};

        const programmeType = getOptionsLabel(
          programmeTypeOptions,
          programmeChoice?.programmeType,
        );
        const {
          proposedChiefSupervisor,
          proposedChiefSupervisorBackup,
          rejectedByFirstChoice,
        } = programmeChoice ?? {};

        // TODO will add display name  in inWhichSupervisorList
        const inWhichSupervisorList: string =
          !(proposedChiefSupervisor as boolean)
            ? NO_INDICATION
            : !(rejectedByFirstChoice as boolean)
              ? proposedChiefSupervisor
              : !(proposedChiefSupervisorBackup as boolean)
                ? NO_INDICATION
                : proposedChiefSupervisorBackup;

        const fundings = element.nomination?.fundings;
        let displayFundings = fundings?.map((funding: any) => {
          const fundingSource =
            fundingSourceValues[
              funding?.source as keyof typeof fundingSourceValues
            ];
          const fundingStatus =
            FUNDING_STATUS_DISPLAY_STRING[
              funding?.status as keyof typeof FUNDING_STATUS_DISPLAY_STRING
            ] ?? 'NA';

          return `${fundingSource} | ${fundingStatus}`;
        });
        if (!displayFundings?.length) {
          displayFundings = ['N/A'];
        }

        const lastNominationSubmissionTime = dayjs(
          element.nomination?.submission?.lastSubmissionTime,
        ).format('DD/MM/YYYY, HH:MM:ss');
        return {
          applicationId,
          applicationNo,
          applicantName: `${applicant?.name?.surname?.toUpperCase()} ${applicant?.name.givenName}`,
          progLevel:
            PROGRAMME_LEVEL[
              programmeChoice?.programme as keyof typeof PROGRAMME_LEVEL
            ],
          researchField: programmeChoice?.researchArea,
          studyMode: getOptionsLabel(
            modeOfStudyOptions,
            programmeChoice?.studyMode,
          ),
          department: programmeChoice?.department,
          inWhichSupervisorList,
          programmeType,
          lastNominationSubmissionTime,
          status,
          fundingScheme: displayFundings,
          entryYear: programmeChoice?.entryYear,
          // if no HKPFSReferenceNumber, display '-'
          HKPFSReferenceNumber: hkpfsInformation?.HKPFSReferenceNumber ?? '-',
          validHKPFSApplication:
            hkpfsInformation?.validHKPFSApplication ?? null, // can be boolean or null
        };
      }) as DRCNonProposedData[],
      totalPage:
        applicationsData?.applicationViewsPaginated?.paginator?.total_pages ??
        1,
      count: applicationsData?.applicationViewsPaginated?.paginator?.count ?? 0,
    };
  }

  return { nonProposedApplications, loading, error, refetch };
};
